// Odesli / song.link API — resolves a song to streaming platform links
// Free, no auth required (10 req/min without key)
// Docs: https://odesli.co/

export interface OdesliPlatformLinks {
  spotify?: string;
  appleMusic?: string;
  youtubeMusic?: string;
  youtube?: string;
  tidal?: string;
  amazonMusic?: string;
  deezer?: string;
  soundcloud?: string;
}

export interface OdesliResult {
  songLinkUrl: string; // universal song.link URL
  platformLinks: OdesliPlatformLinks;
}

export interface OdesliEntityMetadata {
  title: string;
  artistName: string;
  thumbnailUrl?: string;
  apiProvider?: string; // 'itunes', 'spotify', etc.
  id?: string;          // platform-specific ID (e.g. iTunes trackId)
  platforms?: string[];  // ['appleMusic', 'itunes'], etc.
}

export interface OdesliUrlResult {
  songLinkUrl: string;
  platformLinks: OdesliPlatformLinks;
  metadata: OdesliEntityMetadata | null;
}

// Map Odesli platform keys to our friendly names
const PLATFORM_KEY_MAP: Record<string, keyof OdesliPlatformLinks> = {
  spotify: 'spotify',
  appleMusic: 'appleMusic',
  youtubeMusic: 'youtubeMusic',
  youtube: 'youtube',
  tidal: 'tidal',
  amazonMusic: 'amazonMusic',
  deezer: 'deezer',
  soundcloud: 'soundcloud',
};

// Extract platform links from raw Odesli response
function parsePlatformLinks(data: Record<string, unknown>): OdesliPlatformLinks {
  const platformLinks: OdesliPlatformLinks = {};
  const linksByPlatform = (data.linksByPlatform || {}) as Record<string, { url?: string }>;
  for (const [key, mapped] of Object.entries(PLATFORM_KEY_MAP)) {
    const entry = linksByPlatform[key];
    if (entry?.url) {
      platformLinks[mapped] = entry.url;
    }
  }
  return platformLinks;
}

// Extract best entity metadata from Odesli response (prefer iTunes/Apple Music)
function parseEntityMetadata(data: Record<string, unknown>): OdesliEntityMetadata | null {
  const entities = data.entitiesByUniqueId as Record<string, Record<string, unknown>> | undefined;
  if (!entities) return null;
  const values = Object.values(entities);
  // Prefer iTunes/Apple Music entity for richest metadata
  const itunesEntity = values.find(e =>
    e.apiProvider === 'itunes' || (Array.isArray(e.platforms) && e.platforms.includes('itunes'))
  );
  const entity = itunesEntity || values[0];
  if (!entity || !entity.title) return null;
  return {
    title: entity.title as string,
    artistName: entity.artistName as string,
    thumbnailUrl: entity.thumbnailUrl as string | undefined,
    apiProvider: entity.apiProvider as string | undefined,
    id: entity.id != null ? String(entity.id) : undefined,
    platforms: entity.platforms as string[] | undefined,
  };
}

/**
 * Resolve a song (by title + artist) to streaming platform links via Odesli.
 * Uses the iTunes track ID if available for a more reliable lookup.
 */
export async function resolveToAllPlatforms(
  title: string,
  artist: string,
  itunesTrackId?: string | number
): Promise<OdesliResult | null> {
  try {
    let appleMusicUrl: string;

    if (itunesTrackId) {
      appleMusicUrl = `https://music.apple.com/us/album/${itunesTrackId}`;
    } else {
      const searchQuery = encodeURIComponent(`${title} ${artist}`);
      const itunesRes = await fetch(
        `https://itunes.apple.com/search?term=${searchQuery}&entity=song&limit=1`
      );
      if (!itunesRes.ok) return null;
      const itunesData = await itunesRes.json();
      const track = itunesData.results?.[0];
      if (!track?.trackId) return null;
      appleMusicUrl = `https://music.apple.com/us/album/${track.trackId}`;
    }

    // Route through our Cloud Function proxy to avoid CORS issues
    const res = await fetch(`/api/odesli?url=${encodeURIComponent(appleMusicUrl)}`);
    if (!res.ok) return null;
    const data = await res.json();

    return {
      songLinkUrl: data.pageUrl || '',
      platformLinks: parsePlatformLinks(data),
    };
  } catch {
    return null;
  }
}

/**
 * Resolve any music platform URL to cross-platform links + metadata via Odesli.
 * Accepts Spotify, Apple Music, YouTube Music, Tidal, Amazon, Deezer URLs.
 * Routes through our Cloud Function proxy to avoid CORS issues in production.
 */
export async function resolveFromUrl(platformUrl: string): Promise<OdesliUrlResult | null> {
  try {
    // Use our Cloud Function proxy to avoid CORS issues with the Odesli API
    const res = await fetch(
      `/api/odesli?url=${encodeURIComponent(platformUrl)}`
    );
    if (!res.ok) return null;
    const data = await res.json();

    return {
      songLinkUrl: data.pageUrl || '',
      platformLinks: parsePlatformLinks(data),
      metadata: parseEntityMetadata(data),
    };
  } catch {
    return null;
  }
}

/**
 * Get a human-readable label for a detected playerName string.
 */
export function getPlatformLabel(playerName: string | undefined): string {
  if (!playerName) return 'your music app';
  const lower = playerName.toLowerCase();
  if (lower.includes('spotify')) return 'Spotify';
  if (lower.includes('apple') || lower.includes('music')) return 'Apple Music';
  if (lower.includes('youtube')) return 'YouTube Music';
  if (lower.includes('tidal')) return 'Tidal';
  if (lower.includes('amazon')) return 'Amazon Music';
  if (lower.includes('deezer')) return 'Deezer';
  return playerName;
}

/**
 * Get the best platform link for the DJ's active player, with fallbacks.
 */
export function getBestPlatformLink(
  links: OdesliPlatformLinks,
  playerName: string | undefined
): { url: string; platform: string } | null {
  const lower = (playerName || '').toLowerCase();

  // Try to match detected player first
  if (lower.includes('spotify') && links.spotify) {
    return { url: links.spotify, platform: 'Spotify' };
  }
  if ((lower.includes('apple') || lower.includes('music')) && links.appleMusic) {
    return { url: links.appleMusic, platform: 'Apple Music' };
  }
  if (lower.includes('youtube') && links.youtubeMusic) {
    return { url: links.youtubeMusic, platform: 'YouTube Music' };
  }
  if (lower.includes('tidal') && links.tidal) {
    return { url: links.tidal, platform: 'Tidal' };
  }
  if (lower.includes('amazon') && links.amazonMusic) {
    return { url: links.amazonMusic, platform: 'Amazon Music' };
  }
  if (lower.includes('deezer') && links.deezer) {
    return { url: links.deezer, platform: 'Deezer' };
  }

  // Fallback priority order
  if (links.spotify) return { url: links.spotify, platform: 'Spotify' };
  if (links.appleMusic) return { url: links.appleMusic, platform: 'Apple Music' };
  if (links.youtubeMusic) return { url: links.youtubeMusic, platform: 'YouTube Music' };
  if (links.tidal) return { url: links.tidal, platform: 'Tidal' };
  if (links.amazonMusic) return { url: links.amazonMusic, platform: 'Amazon Music' };
  if (links.deezer) return { url: links.deezer, platform: 'Deezer' };
  if (links.soundcloud) return { url: links.soundcloud, platform: 'SoundCloud' };

  return null;
}

/**
 * Generate a direct search URL for a given platform (fallback if Odesli fails).
 */
export function getPlatformSearchUrl(playerName: string | undefined, title: string, artist: string): string {
  const q = encodeURIComponent(`${title} ${artist}`);
  const lower = (playerName || '').toLowerCase();
  // Spotify: use app URI to avoid browser playback takeover
  if (lower.includes('spotify')) return `spotify:search:${q}`;
  if (lower.includes('apple') || lower.includes('music')) return `music://music.apple.com/search?term=${q}`;
  if (lower.includes('youtube')) return `https://music.youtube.com/search?q=${q}`;
  if (lower.includes('tidal')) return `https://tidal.com/search?q=${q}`;
  if (lower.includes('amazon')) return `https://music.amazon.com/search/${q}`;
  if (lower.includes('deezer')) return `https://www.deezer.com/search/${q}`;
  // Generic fallback
  return `https://music.apple.com/us/search?term=${q}`;
}
